import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { trackFeatureUsage, trackEvent } from '../utils/analytics';
import { formatDateForAPI } from '../utils/dateFormat';
import CustomerAcquisitionReport from '../components/CustomerAcquisitionReport';

const Reports = () => {
  const { t } = useLanguage();
  const [activeReport, setActiveReport] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  
  // Attendance report state
  const [attendanceMonth, setAttendanceMonth] = useState(new Date().getMonth() + 1);
  const [attendanceYear, setAttendanceYear] = useState(new Date().getFullYear());
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  
  // Profit report state
  const [profitReportData, setProfitReportData] = useState(null);
  const [profitLoading, setProfitLoading] = useState(false);

  // Cashback report state
  const [cashbackReportData, setCashbackReportData] = useState([]);
  const [cashbackLoading, setCashbackLoading] = useState(false);

  const parseAmount = (value) => {
    const parsed = parseFloat(String(value ?? 0).replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatAmount = (value) => parseAmount(value).toFixed(2);
  const formatReportDate = (value) => {
    if (!value || value === '-') return '-';

    if (/^\d{2}-\d{2}-\d{4}$/.test(value)) return value;

    const isoMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
    }

    const slashMatch = String(value).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      return `${slashMatch[1].padStart(2, '0')}-${slashMatch[2].padStart(2, '0')}-${slashMatch[3]}`;
    }

    return value;
  };
  const escapeCsvValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const getInvoiceStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'var(--success)';
      case 'CANCELLED': return 'var(--gray-dark)';
      case 'OVERDUE': return 'var(--error)';
      default: return 'var(--warning)';
    }
  };

  const isProductProfitType = (itemType) => String(itemType || '').toUpperCase() === 'PRODUCT';
  const isServiceProfitType = (itemType) => ['SER', 'SERVICE'].includes(String(itemType || '').toUpperCase());
  const getProfitTypeLabel = (itemType, compact = false) => {
    if (isProductProfitType(itemType)) {
      return compact ? t('Products') : t('Product Sales');
    }

    if (isServiceProfitType(itemType)) {
      return compact ? t('Services') : t('Service Sales');
    }

    return compact ? t('Programs') : t('Program Sales');
  };

  const getProfitTypeIcon = (itemType) => {
    if (isProductProfitType(itemType)) return '📦';
    if (isServiceProfitType(itemType)) return '🧾';
    return '🎯';
  };

  const handleGenerateInvoiceReport = async (e) => {
    e.preventDefault();
    
    if (!dateRange.startDate || !dateRange.endDate) {
      setError(t('Please select both start and end dates'));
      return;
    }

    // Validate date range (max 1 year)
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    
    if (daysDiff > 365) {
      setError(t('Date range cannot exceed more than one year'));
      return;
    }

    if (startDate > endDate) {
      setError(t('Start date cannot be after end date'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Format dates to DD-MM-YYYY
      const formattedStartDate = formatDateForAPI(dateRange.startDate);
      const formattedEndDate = formatDateForAPI(dateRange.endDate);

      const response = await axios.get('/api/reports/invoices', {
        params: {
          startDate: formattedStartDate,
          endDate: formattedEndDate
        }
      });

      setReportData(response.data);
      setActiveReport('invoices');
      
      // Track report generation
      trackEvent('report_generated', {
        event_category: 'Reports',
        event_label: 'Invoice Report',
        date_range_days: daysDiff,
        records_count: response.data.length
      });
    } catch (error) {
      console.error('Failed to generate invoice report:', error);
      setError(error.response?.data?.message || t('Failed to generate report. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAttendanceReport = async (e) => {
    e.preventDefault();
    
    setAttendanceLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/attendance/report/summary', {
        params: {
          month: attendanceMonth,
          year: attendanceYear
        }
      });

      setAttendanceSummary(response.data);
      
      // Track report generation
      trackEvent('report_generated', {
        event_category: 'Reports',
        event_label: 'Attendance Report',
        month: attendanceMonth,
        year: attendanceYear,
        customers_count: response.data.rows.length
      });
    } catch (error) {
      console.error('Failed to generate attendance report:', error);
      setError(error.response?.data?.message || t('Failed to generate attendance report. Please try again.'));
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleGenerateProfitReport = async (e) => {
    e.preventDefault();
    
    if (!dateRange.startDate || !dateRange.endDate) {
      setError(t('Please select both start and end dates'));
      return;
    }

    // Validate date range (max 1 year)
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    
    if (daysDiff > 365) {
      setError(t('Date range cannot exceed more than one year'));
      return;
    }

    if (startDate > endDate) {
      setError(t('Start date cannot be after end date'));
      return;
    }

    setProfitLoading(true);
    setError('');

    try {
      // Format dates to DD-MM-YYYY
      const formattedStartDate = formatDateForAPI(dateRange.startDate);
      const formattedEndDate = formatDateForAPI(dateRange.endDate);

      const response = await axios.get('/api/reports/profit', {
        params: {
          startDate: formattedStartDate,
          endDate: formattedEndDate
        }
      });

      setProfitReportData(response.data);
      setActiveReport('profit');
      
      // Track report generation
      trackEvent('report_generated', {
        event_category: 'Reports',
        event_label: 'Profit Report',
        date_range_days: daysDiff,
        total_volume_points: response.data.totalVolumePoints,
        total_profit: response.data.totalProfit,
        total_membership_discount: response.data.totalMembershipDiscount,
        total_custom_discount: response.data.totalCustomDiscount
      });
    } catch (error) {
      console.error('Failed to generate profit report:', error);
      setError(error.response?.data?.message || t('Failed to generate profit report. Please try again.'));
    } finally {
      setProfitLoading(false);
    }
  };

  const handleGenerateCashbackReport = async (e) => {
    e.preventDefault();

    if (!dateRange.startDate || !dateRange.endDate) {
      setError(t('Please select both start and end dates'));
      return;
    }

    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

    if (daysDiff > 365) {
      setError(t('Date range cannot exceed more than one year'));
      return;
    }

    if (startDate > endDate) {
      setError(t('Start date cannot be after end date'));
      return;
    }

    setCashbackLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/reports/cashback', {
        params: {
          startDate: formatDateForAPI(dateRange.startDate),
          endDate: formatDateForAPI(dateRange.endDate)
        }
      });

      setCashbackReportData(response.data || []);
      setActiveReport('cashback');

      trackEvent('report_generated', {
        event_category: 'Reports',
        event_label: 'Cashback Report',
        date_range_days: daysDiff,
        records_count: response.data?.length || 0
      });
    } catch (error) {
      console.error('Failed to generate cashback report:', error);
      setError(error.response?.data?.message || t('Failed to generate cashback report. Please try again.'));
    } finally {
      setCashbackLoading(false);
    }
  };

  const exportCashbackCSV = () => {
    if (cashbackReportData.length === 0) {
      setError(t('No data to export'));
      return;
    }

    const headers = ['Date', 'Customer', 'Amount', 'Source', 'Description', 'Program', 'Referred Customer', 'Invoice', 'Created By'];
    const csvContent = [
      headers.join(','),
      ...cashbackReportData.map(row => [
        formatReportDate(row.createdAt),
        escapeCsvValue(row.customerName),
        row.cashbackAmount ?? 0,
        row.source || 'SYSTEM',
        escapeCsvValue(row.description || ''),
        escapeCsvValue(row.programName || ''),
        escapeCsvValue(row.referredCustomerName || ''),
        escapeCsvValue(row.invoiceNumber || ''),
        escapeCsvValue(row.createdByName || '')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `cashback_report_${dateRange.startDate}_to_${dateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    trackEvent('report_exported', {
      event_category: 'Reports',
      event_label: 'Cashback Report CSV',
      records_count: cashbackReportData.length
    });
  };

  const exportAttendanceCSV = async () => {
    try {
      const response = await axios.get('/api/attendance/report/export', {
        params: {
          month: attendanceMonth,
          year: attendanceYear
        },
        responseType: 'blob'
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const monthName = new Date(attendanceYear, attendanceMonth - 1).toLocaleString('default', { month: 'long' });
      const filename = `attendance-report-${monthName.toLowerCase()}-${attendanceYear}.csv`;
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      trackEvent('report_exported', {
        event_category: 'Reports',
        event_label: 'Attendance Report CSV',
        month: attendanceMonth,
        year: attendanceYear
      });
    } catch (error) {
      console.error('Failed to export attendance CSV:', error);
      setError(t('Failed to export attendance report. Please try again.'));
    }
  };

  const exportToCSV = () => {
    if (reportData.length === 0) {
      setError(t('No data to export'));
      return;
    }

    const headers = [
      'Invoice ID',
      'Invoice Date',
      'Status',
      'Customer Name',
      'GST Number',
      'Amount Paid',
      'Mode of Payment',
      'Payment Date',
      '% GST',
      'Taxable Value',
      'CGST Amount',
      'SGST Amount', 
      'Total of Invoice',
      'Volume Points',
      'GST Codes'
    ];

    const csvContent = [
      headers.join(','),
      ...reportData.map(row => [
        row.invoiceId,
        formatReportDate(row.invoiceDate),
        row.status || 'PENDING',
        escapeCsvValue(row.customerName),
        escapeCsvValue(row.customerGstNumber || ''),
        row.amountPaid,
        row.modeOfPayment,
        formatReportDate(row.paymentDate),
        row.gstPercentage ?? '',
        row.taxableValue ?? 0,
        row.cgstAmount,
        row.sgstAmount,
        row.totalAmount,
        row.volumePoints,
        escapeCsvValue(row.gstCodes || '')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `invoice_report_${dateRange.startDate}_to_${dateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    trackEvent('report_exported', {
      event_category: 'Reports',
      event_label: 'Invoice Report CSV',
      records_count: reportData.length
    });
  };

  React.useEffect(() => {
    trackFeatureUsage('Reports', 'view');
  }, []);

  return (
    <div className="page">
      <h1 className="page-title">{t('Reports')}</h1>
      
      <div style={{
        marginBottom: '20px',
        padding: '15px',
        background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
        color: 'white',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>
          📊 {t('Reports Dashboard')}
        </div>
        <div style={{ fontSize: '14px', opacity: '0.9' }}>
          {t('Generate detailed reports for your business analysis')}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {/* Report Options */}
      <div className="card" style={{ marginBottom: '30px' }}>
        <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
          {t('Available Reports')}
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          {/* Invoice Reports Card */}
          <div style={{
            border: '2px solid var(--primary-blue)',
            borderRadius: '8px',
            padding: '20px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            background: activeReport === 'invoices' ? 'var(--pale-blue)' : 'var(--white)'
          }}
          onClick={() => setActiveReport('invoices')}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            <div style={{ fontSize: '32px', textAlign: 'center', marginBottom: '15px' }}>
              📄
            </div>
            <h4 style={{ color: 'var(--dark-blue)', textAlign: 'center', marginBottom: '10px' }}>
              {t('Invoice Reports')}
            </h4>
            <p style={{ color: 'var(--gray-dark)', textAlign: 'center', fontSize: '14px' }}>
              {t('Generate detailed invoice reports with payment information, GST breakdown, and volume points')}
            </p>
          </div>
          
          {/* Attendance Reports Card */}
          <div style={{
            border: '2px solid var(--primary-blue)',
            borderRadius: '8px',
            padding: '20px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            background: activeReport === 'attendance' ? 'var(--pale-blue)' : 'var(--white)'
          }}
          onClick={() => setActiveReport('attendance')}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            <div style={{ fontSize: '32px', textAlign: 'center', marginBottom: '15px' }}>
              📋
            </div>
            <h4 style={{ color: 'var(--dark-blue)', textAlign: 'center', marginBottom: '10px' }}>
              {t('Attendance Reports')}
            </h4>
            <p style={{ color: 'var(--gray-dark)', textAlign: 'center', fontSize: '14px' }}>
              {t('Generate monthly attendance reports for your club customers')}
            </p>
          </div>

          {/* Profit Reports Card */}
          <div style={{
            border: '2px solid var(--primary-blue)',
            borderRadius: '8px',
            padding: '20px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            background: activeReport === 'profit' ? 'var(--pale-blue)' : 'var(--white)'
          }}
          onClick={() => setActiveReport('profit')}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            <div style={{ fontSize: '32px', textAlign: 'center', marginBottom: '15px' }}>
              💰
            </div>
            <h4 style={{ color: 'var(--dark-blue)', textAlign: 'center', marginBottom: '10px' }}>
              {t('Profit Reports')}
            </h4>
            <p style={{ color: 'var(--gray-dark)', textAlign: 'center', fontSize: '14px' }}>
              {t('Analyze profit margins and volume points distribution across products and programs')}
            </p>
          </div>

          {/* Cashback Reports Card */}
          <div style={{
            border: '2px solid var(--primary-blue)',
            borderRadius: '8px',
            padding: '20px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            background: activeReport === 'cashback' ? 'var(--pale-blue)' : 'var(--white)'
          }}
          onClick={() => setActiveReport('cashback')}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            <div style={{ fontSize: '32px', textAlign: 'center', marginBottom: '15px' }}>
              ₹
            </div>
            <h4 style={{ color: 'var(--dark-blue)', textAlign: 'center', marginBottom: '10px' }}>
              {t('Cashback Reports')}
            </h4>
            <p style={{ color: 'var(--gray-dark)', textAlign: 'center', fontSize: '14px' }}>
              {t('Review custom and system-generated cashback credited to customers')}
            </p>
          </div>

          {/* Customer Acquisition Report Card */}
          <div style={{
            border: '2px solid var(--primary-blue)',
            borderRadius: '8px',
            padding: '20px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            background: activeReport === 'customer-acquisition' ? 'var(--pale-blue)' : 'var(--white)'
          }}
          onClick={() => setActiveReport('customer-acquisition')}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <div style={{ fontSize: '32px', textAlign: 'center', marginBottom: '15px' }}>📈</div>
            <h4 style={{ color: 'var(--dark-blue)', textAlign: 'center', marginBottom: '10px' }}>
              {t('Customer Acquisition')}
            </h4>
            <p style={{ color: 'var(--gray-dark)', textAlign: 'center', fontSize: '14px' }}>
              {t('Analyze customer growth from actively subscribed coaches across your hierarchy')}
            </p>
          </div>

          {/* Future Reports Placeholder */}
          <div style={{
            border: '2px dashed var(--gray-medium)',
            borderRadius: '8px',
            padding: '20px',
            background: 'var(--gray-light)',
            opacity: '0.6'
          }}>
            <div style={{ fontSize: '32px', textAlign: 'center', marginBottom: '15px' }}>
              🚧
            </div>
            <h4 style={{ color: 'var(--gray-dark)', textAlign: 'center', marginBottom: '10px' }}>
              {t('More Reports Coming Soon')}
            </h4>
            <p style={{ color: 'var(--gray-dark)', textAlign: 'center', fontSize: '14px' }}>
              {t('Additional report types will be added in future updates')}
            </p>
          </div>
        </div>
      </div>

      {/* Invoice Report Section */}
      {activeReport === 'invoices' && (
        <div className="card">
          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
            {t('Invoice Report Generator')}
          </h3>

          <form onSubmit={handleGenerateInvoiceReport} style={{ marginBottom: '30px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '15px', alignItems: 'end' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="startDate" className="form-label">
                  {t('Start Date')} *
                </label>
                <input
                  type="date"
                  id="startDate"
                  className="form-input"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="endDate" className="form-label">
                  {t('End Date')} *
                </label>
                <input
                  type="date"
                  id="endDate"
                  className="form-input"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                  required
                  disabled={loading}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? t('Generating...') : t('Generate Report')}
                </button>
                
                {reportData.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={exportToCSV}
                    disabled={loading}
                  >
                    {t('Export CSV')}
                  </button>
                )}
              </div>
            </div>

            <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '10px' }}>
              {t('Note: Date range cannot exceed more than one year')}
            </div>
          </form>

          {/* Report Results */}
          {reportData.length > 0 && (
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '20px' 
              }}>
                <h4 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                  {t('Invoice Report Results ({count} entries)', { count: reportData.length })}
                </h4>
                <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
                  {t('Period')}: {formatDateForAPI(dateRange.startDate)} {t('to')} {formatDateForAPI(dateRange.endDate)}
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '12px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Invoice ID')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Invoice Date')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Status')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Customer Name')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('GST Number')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Amount Paid')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Mode of Payment')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Payment Date')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('% GST')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Taxable Value')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('CGST Amount')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('SGST Amount')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Total of Invoice')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Volume Points')}
                      </th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('GST Codes')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((row, index) => (
                      <tr 
                        key={`${row.invoiceId}-${index}`}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                          borderBottom: '1px solid var(--gray-medium)'
                        }}
                      >
                        <td style={{ padding: '8px', fontWeight: '500' }}>
                          {row.invoiceId}
                        </td>
                        <td style={{ padding: '8px' }}>
                          {formatReportDate(row.invoiceDate)}
                        </td>
                        <td style={{ padding: '8px' }}>
                          <span style={{
                            background: getInvoiceStatusColor(row.status),
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '10px'
                          }}>
                            {t(row.status || 'PENDING')}
                          </span>
                        </td>
                        <td style={{ padding: '8px' }}>
                          {row.customerName}
                        </td>
                        <td style={{ padding: '8px' }}>
                          {row.customerGstNumber || '-'}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: '500' }}>
                          ₹{row.amountPaid}
                        </td>
                        <td style={{ padding: '8px' }}>
                          <span style={{
                            background: row.modeOfPayment === 'UNPAID' ? 'var(--error)' : 'var(--success)',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '10px'
                          }}>
                            {row.modeOfPayment}
                          </span>
                        </td>
                        <td style={{ padding: '8px' }}>
                          {formatReportDate(row.paymentDate)}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>
                          {row.gstPercentage != null ? `${row.gstPercentage}%` : '-'}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>
                          ₹{row.taxableValue ?? 0}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>
                          ₹{row.cgstAmount}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>
                          ₹{row.sgstAmount}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: '500' }}>
                          ₹{row.totalAmount}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>
                          {row.volumePoints}
                        </td>
                        <td style={{ padding: '8px' }}>
                          {row.gstCodes || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div style={{
                marginTop: '20px',
                padding: '15px',
                background: 'var(--pale-blue)',
                borderRadius: '8px',
                border: '2px solid var(--primary-blue)'
              }}>
                <h5 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                  {t('Report Summary')}
                </h5>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--primary-blue)' }}>
                      {reportData.length}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{t('Total Entries')}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--primary-blue)' }}>
                      ₹{reportData.reduce((sum, row) => sum + parseFloat(row.amountPaid || 0), 0).toFixed(2)}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{t('Total Payments')}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--primary-blue)' }}>
                      ₹{reportData.reduce((sum, row) => sum + parseFloat(row.cgstAmount || 0), 0).toFixed(2)}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{t('Total CGST')}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--primary-blue)' }}>
                      ₹{reportData.reduce((sum, row) => sum + parseFloat(row.sgstAmount || 0), 0).toFixed(2)}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{t('Total SGST')}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Attendance Report Section */}
      {activeReport === 'attendance' && (
        <div className="card">
          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
            {t('Attendance Report Generator')}
          </h3>

          <form onSubmit={handleGenerateAttendanceReport} style={{ marginBottom: '30px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '15px', alignItems: 'end' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="attendanceMonth" className="form-label">
                  {t('Month')} *
                </label>
                <select
                  id="attendanceMonth"
                  className="form-input"
                  value={attendanceMonth}
                  onChange={(e) => setAttendanceMonth(parseInt(e.target.value))}
                  required
                  disabled={attendanceLoading}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="attendanceYear" className="form-label">
                  {t('Year')} *
                </label>
                <select
                  id="attendanceYear"
                  className="form-input"
                  value={attendanceYear}
                  onChange={(e) => setAttendanceYear(parseInt(e.target.value))}
                  required
                  disabled={attendanceLoading}
                >
                  {Array.from({ length: 11 }, (_, i) => {
                    const year = new Date().getFullYear() - 5 + i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={attendanceLoading}
                >
                  {attendanceLoading ? t('Generating...') : t('Generate Report')}
                </button>
                
                {attendanceSummary && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={exportAttendanceCSV}
                    disabled={attendanceLoading}
                  >
                    {t('Export CSV')}
                  </button>
                )}
              </div>
            </div>
          </form>

          {/* Attendance Report Results */}
          {attendanceSummary && (
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '20px' 
              }}>
                <h4 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                  {t('Attendance Summary ({count} customers)', { count: attendanceSummary.rows.length })}
                </h4>
                <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
                  {attendanceSummary.companyName} • {new Date(attendanceSummary.year, attendanceSummary.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })} • {attendanceSummary.daysInMonth} days
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Customer Name')}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Present Days')}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Absent Days')}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>
                        {t('Attendance %')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceSummary.rows.map((row, index) => {
                      const attendancePercentage = ((row.present / attendanceSummary.daysInMonth) * 100).toFixed(1);
                      return (
                        <tr 
                          key={row.userId}
                          style={{ 
                            backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                            borderBottom: '1px solid var(--gray-medium)'
                          }}
                        >
                          <td style={{ padding: '12px', fontWeight: '500' }}>
                            {row.name}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <span style={{
                              background: 'var(--success)',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {row.present}
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <span style={{
                              background: row.absent > 0 ? 'var(--error)' : 'var(--gray-medium)',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {row.absent}
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                            <span style={{
                              color: attendancePercentage >= 80 ? 'var(--success)' : 
                                     attendancePercentage >= 60 ? 'var(--warning)' : 'var(--error)'
                            }}>
                              {attendancePercentage}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Summary Statistics */}
              <div style={{
                marginTop: '20px',
                padding: '15px',
                background: 'var(--pale-blue)',
                borderRadius: '8px',
                border: '2px solid var(--primary-blue)'
              }}>
                <h5 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                  {t('Report Summary')}
                </h5>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--primary-blue)' }}>
                      {attendanceSummary.rows.length}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{t('Total Customers')}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--success)' }}>
                      {attendanceSummary.rows.reduce((sum, row) => sum + row.present, 0)}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{t('Total Present')}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--error)' }}>
                      {attendanceSummary.rows.reduce((sum, row) => sum + row.absent, 0)}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{t('Total Absent')}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--primary-blue)' }}>
                      {attendanceSummary.daysInMonth}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{t('Days in Month')}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Profit Report Section */}
      {activeReport === 'profit' && (
        <div className="card">
          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
            {t('Profit & Volume Points Report')}
          </h3>

          <form onSubmit={handleGenerateProfitReport} style={{ marginBottom: '30px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '15px', alignItems: 'end' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="profitStartDate" className="form-label">
                  {t('Start Date')} *
                </label>
                <input
                  type="date"
                  id="profitStartDate"
                  className="form-input"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                  required
                  disabled={profitLoading}
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="profitEndDate" className="form-label">
                  {t('End Date')} *
                </label>
                <input
                  type="date"
                  id="profitEndDate"
                  className="form-input"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                  required
                  disabled={profitLoading}
                />
              </div>

              <div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={profitLoading}
                >
                  {profitLoading ? t('Generating...') : t('Generate Report')}
                </button>
              </div>
            </div>

            <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '10px' }}>
              {t('Note: Date range cannot exceed more than one year')}
            </div>
          </form>

          {/* Profit Report Results */}
          {profitReportData && (
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '20px' 
              }}>
                <h4 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                  {t('Profit Analysis Report')}
                </h4>
                <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
                  {t('Period')}: {profitReportData.startDate} {t('to')} {profitReportData.endDate}
                </div>
              </div>

              {/* Summary Cards */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', 
                gap: '15px',
                marginBottom: '30px'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    ₹{formatAmount(profitReportData.totalProfit)}
                  </div>
                  <div style={{ fontSize: '14px', opacity: '0.9' }}>{t('Total Profit')}</div>
                </div>
                
                <div style={{
                  background: 'linear-gradient(135deg, #4A90E2 0%, #2C5282 100%)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {profitReportData.totalVolumePoints}
                  </div>
                  <div style={{ fontSize: '14px', opacity: '0.9' }}>{t('Total Volume Points')}</div>
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #805AD5 0%, #553C9A 100%)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    ₹{profitReportData.totalCostToCompany || '0.00'}
                  </div>
                  <div style={{ fontSize: '14px', opacity: '0.9' }}>{t('Total Cost to Company')}</div>
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.35)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '13px', marginBottom: '6px' }}>
                      <span>{t('Product Cost')}</span>
                      <strong>₹{profitReportData.productCostToCompany || '0.00'}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '13px' }}>
                      <span>{t('Program Cost')}</span>
                      <strong>₹{profitReportData.programCostToCompany || '0.00'}</strong>
                    </div>
                  </div>
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #38B2AC 0%, #2C7A7B 100%)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    ₹{profitReportData.totalPipelineRevenue || '0.00'}
                  </div>
                  <div style={{ fontSize: '14px', opacity: '0.9' }}>{t('Sales in Pipeline')}</div>
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.35)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '13px', marginBottom: '6px' }}>
                      <span>{t('Program Renewal Sales')}</span>
                      <strong>₹{profitReportData.pipelineRenewalRevenue || '0.00'}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '13px' }}>
                      <span>{t('Payment Due')}</span>
                      <strong>₹{profitReportData.pipelinePendingDues || '0.00'}</strong>
                    </div>
                  </div>
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #F6AD55 0%, #ED8936 100%)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    ₹{profitReportData.totalMembershipDiscount}
                  </div>
                  <div style={{ fontSize: '14px', opacity: '0.9' }}>{t('Membership Discounts')}</div>
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #FC8181 0%, #E53E3E 100%)',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    ₹{profitReportData.totalCustomDiscount}
                  </div>
                  <div style={{ fontSize: '14px', opacity: '0.9' }}>{t('Custom Discounts')}</div>
                </div>
              </div>

              {/* Charts Section */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                {/* Profit Bar Chart */}
                <div style={{
                  border: '2px solid var(--light-blue)',
                  borderRadius: '8px',
                  padding: '20px',
                  background: 'var(--white)'
                }}>
                  <h5 style={{ color: 'var(--dark-blue)', marginBottom: '15px', textAlign: 'center' }}>
                    💰 {t('Profit Distribution')}
                  </h5>
                  
                  {profitReportData.profitDistribution.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                      {t('No profit data available for this period')}
                    </div>
                  ) : (
                    <div>
                      {/* Find max profit for scaling */}
                      {(() => {
                        const maxProfit = Math.max(...profitReportData.profitDistribution.map(item => parseAmount(item.profit)));
                        return profitReportData.profitDistribution.map((item, index) => (
                          <div key={`${item.itemType}-${index}`} style={{ marginBottom: '20px' }}>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              marginBottom: '8px'
                            }}>
                              <span style={{ fontWeight: '500' }}>
                                {getProfitTypeIcon(item.itemType)} {getProfitTypeLabel(item.itemType)}
                              </span>
                              <span style={{ fontWeight: 'bold', color: 'var(--success)' }}>
                                ₹{formatAmount(item.profit)}
                              </span>
                            </div>
                            <div style={{
                              width: '100%',
                              height: '30px',
                              background: 'var(--gray-light)',
                              borderRadius: '15px',
                              overflow: 'hidden',
                              position: 'relative'
                            }}>
                              <div style={{
                                width: `${maxProfit > 0 ? (parseAmount(item.profit) / maxProfit) * 100 : 0}%`,
                                height: '100%',
                                background: isProductProfitType(item.itemType) ? 
                                  'linear-gradient(135deg, #4A90E2 0%, #2C5282 100%)' : 
                                  'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                                transition: 'width 0.8s ease',
                                borderRadius: '15px'
                              }}></div>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>

                {/* Volume Points Distribution */}
                <div style={{
                  border: '2px solid var(--light-blue)',
                  borderRadius: '8px',
                  padding: '20px',
                  background: 'var(--white)'
                }}>
                  <h5 style={{ color: 'var(--dark-blue)', marginBottom: '15px', textAlign: 'center' }}>
                    📊 {t('Volume Points Distribution')}
                  </h5>
                  
                  {profitReportData.volumePointsDistribution.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                      {t('No volume points data available for this period')}
                    </div>
                  ) : (
                    <div>
                      {/* Simple visual representation */}
                      {profitReportData.volumePointsDistribution.map((item, index) => (
                        <div key={item.itemType} style={{ marginBottom: '15px' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '5px'
                          }}>
                            <span style={{ fontWeight: '500' }}>
                              {getProfitTypeIcon(item.itemType)} {getProfitTypeLabel(item.itemType, true)}
                            </span>
                            <span style={{ fontWeight: 'bold' }}>
                              {formatAmount(item.volumePoints)} ({parseAmount(item.percentage).toFixed(1)}%)
                            </span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '20px',
                            background: 'var(--gray-light)',
                            borderRadius: '10px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${parseAmount(item.percentage)}%`,
                              height: '100%',
                              background: isProductProfitType(item.itemType) ? 
                                'linear-gradient(135deg, #4A90E2 0%, #2C5282 100%)' : 
                                'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                              transition: 'width 0.5s ease'
                            }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Discount Distribution */}
                <div style={{
                  border: '2px solid var(--light-blue)',
                  borderRadius: '8px',
                  padding: '20px',
                  background: 'var(--white)'
                }}>
                  <h5 style={{ color: 'var(--dark-blue)', marginBottom: '15px', textAlign: 'center' }}>
                    🎫 {t('Discount Distribution')}
                  </h5>
                  
                  {profitReportData.discountDistribution.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                      {t('No discount data available for this period')}
                    </div>
                  ) : (
                    <div>
                      {profitReportData.discountDistribution.map((item, index) => (
                        <div key={item.discountType} style={{ marginBottom: '15px' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '5px'
                          }}>
                            <span style={{ fontWeight: '500' }}>
                              {item.discountType === 'MEMBERSHIP' ? `👥 ${t('Membership Discounts')}` : `✏️ ${t('Custom Discounts')}`}
                            </span>
                            <span style={{ fontWeight: 'bold', color: 'var(--warning)' }}>
                              ₹{item.discountAmount} ({item.percentage}%)
                            </span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '20px',
                            background: 'var(--gray-light)',
                            borderRadius: '10px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${item.percentage}%`,
                              height: '100%',
                              background: item.discountType === 'MEMBERSHIP' ? 
                                'linear-gradient(135deg, #F6AD55 0%, #ED8936 100%)' : 
                                'linear-gradient(135deg, #FC8181 0%, #E53E3E 100%)',
                              transition: 'width 0.5s ease'
                            }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Detailed Breakdown */}
              <div style={{
                marginTop: '30px',
                padding: '20px',
                background: 'var(--pale-blue)',
                borderRadius: '8px',
                border: '2px solid var(--primary-blue)'
              }}>
                <h5 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
                  📈 {t('Detailed Breakdown')}
                </h5>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
                  <div>
                    <h6 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>{t('Volume Points')}</h6>
                    {profitReportData.volumePointsDistribution.map(item => (
                      <div key={item.itemType} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        marginBottom: '5px',
                        fontSize: '14px'
                      }}>
                        <span>{getProfitTypeLabel(item.itemType, true)}:</span>
                        <span style={{ fontWeight: 'bold' }}>{formatAmount(item.volumePoints)} pts</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h6 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>{t('Profit Margins')}</h6>
                    {profitReportData.profitDistribution.map(item => (
                      <div key={item.itemType} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        marginBottom: '5px',
                        fontSize: '14px'
                      }}>
                        <span>{getProfitTypeLabel(item.itemType, true)}:</span>
                        <span style={{ fontWeight: 'bold', color: 'var(--success)' }}>₹{formatAmount(item.profit)}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h6 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>{t('Discount Breakdown')}</h6>
                    {profitReportData.discountDistribution.map(item => (
                      <div key={item.discountType} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        marginBottom: '5px',
                        fontSize: '14px'
                      }}>
                        <span>{item.discountType === 'MEMBERSHIP' ? t('Membership') : t('Custom')}:</span>
                        <span style={{ fontWeight: 'bold', color: 'var(--warning)' }}>₹{item.discountAmount}</span>
                      </div>
                    ))}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginTop: '10px',
                      paddingTop: '8px',
                      borderTop: '1px solid var(--gray-medium)',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      <span>{t('Total Discounts')}:</span>
                      <span style={{ color: 'var(--warning)' }}>
                        ₹{(parseFloat(profitReportData.totalMembershipDiscount) + parseFloat(profitReportData.totalCustomDiscount)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <h6 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>{t('Cost to Company Breakdown')}</h6>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginBottom: '5px',
                      fontSize: '14px'
                    }}>
                      <span>{t('Products')}:</span>
                      <span style={{ fontWeight: 'bold', color: 'var(--primary-blue)' }}>₹{profitReportData.productCostToCompany || '0.00'}</span>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginBottom: '5px',
                      fontSize: '14px'
                    }}>
                      <span>{t('Programs')}:</span>
                      <span style={{ fontWeight: 'bold', color: 'var(--primary-blue)' }}>₹{profitReportData.programCostToCompany || '0.00'}</span>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginTop: '10px',
                      paddingTop: '8px',
                      borderTop: '1px solid var(--gray-medium)',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      <span>{t('Total Cost to Company')}:</span>
                      <span style={{ color: 'var(--primary-blue)' }}>₹{profitReportData.totalCostToCompany || '0.00'}</span>
                    </div>
                  </div>
                  <div>
                    <h6 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>{t('Pipeline Breakdown')}</h6>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginBottom: '5px',
                      fontSize: '14px'
                    }}>
                      <span>{t('Program Renewal Sales')}:</span>
                      <span style={{ fontWeight: 'bold', color: 'var(--success)' }}>₹{profitReportData.pipelineRenewalRevenue || '0.00'}</span>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginBottom: '5px',
                      fontSize: '14px'
                    }}>
                      <span>{t('Pending Dues')}:</span>
                      <span style={{ fontWeight: 'bold', color: 'var(--warning)' }}>₹{profitReportData.pipelinePendingDues || '0.00'}</span>
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginTop: '10px',
                      paddingTop: '8px',
                      borderTop: '1px solid var(--gray-medium)',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      <span>{t('Total Pipeline Revenue')}:</span>
                      <span style={{ color: 'var(--success)' }}>₹{profitReportData.totalPipelineRevenue || '0.00'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Customer Acquisition Report Section */}
      {activeReport === 'customer-acquisition' && (
        <div className="card">
          <CustomerAcquisitionReport />
        </div>
      )}

      {/* Cashback Report Section */}
      {activeReport === 'cashback' && (
        <div className="card">
          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
            {t('Cashback Report')}
          </h3>

          <form onSubmit={handleGenerateCashbackReport} style={{ marginBottom: '30px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '15px', alignItems: 'end' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="cashbackStartDate" className="form-label">{t('Start Date')} *</label>
                <input
                  type="date"
                  id="cashbackStartDate"
                  className="form-input"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  required
                  disabled={cashbackLoading}
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="cashbackEndDate" className="form-label">{t('End Date')} *</label>
                <input
                  type="date"
                  id="cashbackEndDate"
                  className="form-input"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  required
                  disabled={cashbackLoading}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" className="btn btn-primary" disabled={cashbackLoading}>
                  {cashbackLoading ? t('Generating...') : t('Generate Report')}
                </button>
                {cashbackReportData.length > 0 && (
                  <button type="button" className="btn btn-secondary" onClick={exportCashbackCSV} disabled={cashbackLoading}>
                    {t('Export CSV')}
                  </button>
                )}
              </div>
            </div>
          </form>

          {cashbackReportData.length > 0 && (
            <div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '15px',
                marginBottom: '20px'
              }}>
                <div style={{ background: 'var(--pale-blue)', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--primary-blue)' }}>{cashbackReportData.length}</div>
                  <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{t('Total Entries')}</div>
                </div>
                <div style={{ background: 'var(--pale-blue)', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--success)' }}>
                    ₹{cashbackReportData.reduce((sum, row) => sum + parseAmount(row.cashbackAmount), 0).toFixed(2)}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{t('Total Cashback')}</div>
                </div>
                <div style={{ background: 'var(--pale-blue)', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--primary-blue)' }}>
                    ₹{cashbackReportData
                      .filter(row => row.source === 'CUSTOM')
                      .reduce((sum, row) => sum + parseAmount(row.cashbackAmount), 0)
                      .toFixed(2)}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{t('Custom Cashback')}</div>
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Date')}</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Customer')}</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--light-blue)' }}>{t('Amount')}</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Source')}</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Description')}</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Invoice')}</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Created By')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashbackReportData.map((row, index) => (
                      <tr key={`${row.cashbackId}-${index}`} style={{ backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)' }}>
                        <td style={{ padding: '10px' }}>{formatReportDate(row.createdAt)}</td>
                        <td style={{ padding: '10px', fontWeight: '500' }}>{row.customerName}</td>
                        <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: 'var(--success)' }}>
                          ₹{formatAmount(row.cashbackAmount)}
                        </td>
                        <td style={{ padding: '10px' }}>{row.source || 'SYSTEM'}</td>
                        <td style={{ padding: '10px' }}>{row.description || row.programName || '-'}</td>
                        <td style={{ padding: '10px' }}>{row.invoiceNumber || '-'}</td>
                        <td style={{ padding: '10px' }}>{row.createdByName || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;
